import { useCallback } from 'react';

import * as Sentry from '@sentry/react';
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
  type SetterOrUpdater,
} from 'recoil';

import { Chart } from '../../types';

type ChartAtomType = Chart | undefined;

const chartAtom = atom<ChartAtomType>({
  key: 'chartAtom',
  default: undefined,
});

export const useChartAtom = (): [
  ChartAtomType,
  SetterOrUpdater<ChartAtomType>,
] => {
  return useRecoilState<ChartAtomType>(chartAtom);
};

export const useChartValue = (): ChartAtomType => {
  return useRecoilValue<ChartAtomType>(chartAtom);
};

const useSetChart = (): SetterOrUpdater<ChartAtomType> => {
  return useSetRecoilState<ChartAtomType>(chartAtom);
};

export const useSafeSetChart = (): SetterOrUpdater<Chart> => {
  const setChart = useSetChart();
  return useCallback<SetterOrUpdater<Chart>>(
    (valueOrUpdater) => {
      setChart((oldChart, ...rest) => {
        if (!oldChart) {
          Sentry.captureException(
            'Tried to update chart atom when chart is undefined'
          );
          return undefined;
        }
        if (typeof valueOrUpdater === 'function') {
          return valueOrUpdater(oldChart, ...rest);
        }
        return valueOrUpdater;
      });
    },
    [setChart]
  );
};

export default chartAtom;
